/**
 * Denetim kaydı — KK-807, KK-1505.
 *
 * =============================================================================
 * SALT OKUNUR — VE BU BİR ARAYÜZ TERCİHİ DEĞİL
 * -----------------------------------------------------------------------------
 * audit_log tablosunda uygulama rolünün UPDATE/DELETE YETKİSİ YOKTUR (§9.7);
 * sunucuda da yalnızca GET yüzeyi tanımlıdır. Bu ekran bu yüzden hiçbir
 * düzenleme kontrolü içermez: "değiştirilemez, salt eklenir" özelliği
 * kullanıcıya da açıkça söylenir. Bir denetim kaydının yanında duran düzenle
 * düğmesi, o kaydın değerini sıfırlar.
 *
 * FİLTRELER KK-1505'in "panelde filtrelenebilir" şartını karşılar: eylem,
 * aktör tipi, varlık ve tarih aralığı. Tarih "YYYY-MM-DD" verildiğinde gün
 * sınırı OKUL SAAT DİLİMİNDE çözülür (KK-205 / DK-03) — müdür okul gününü
 * kasteder, UTC gününü değil; sunucu bu dönüşümü yapar ve saat dilimini
 * yanıtta bildirir.
 * =============================================================================
 */
import { useState } from "react";
import { Rozet, Tablo, TabloBaslik, TabloGovde, Uyari } from "@kantin/ui";
import { useAudit } from "../../veri/sorgular.js";
import type { AuditFiltresi } from "../../veri/sorgular.js";
import { AKTOR_TIPI_ADI, jsonGoster, tarihSaat } from "../../veri/bicim.js";
import {
  FiltreSeridi,
  MetinSuzgeci,
  Sayfa,
  SayfaBasligi,
  Sayfalayici,
  SecimSuzgeci,
  SorguDurumu,
  TarihAlani,
} from "../../ortak.js";

const AKTOR_SECENEKLERI = [
  { deger: "", metin: "Tüm aktörler" },
  { deger: "personel", metin: "Personel" },
  { deger: "veli", metin: "Veli" },
  { deger: "terminal", metin: "Terminal" },
  { deger: "sistem", metin: "Sistem" },
];

export function Audit() {
  const [filtre, setFiltre] = useState<AuditFiltresi>({});
  const [sayfa, setSayfa] = useState(1);
  const sorgu = useAudit(filtre, sayfa);

  const guncelle = (parca: Partial<AuditFiltresi>) => {
    setFiltre((o) => ({ ...o, ...parca }));
    setSayfa(1);
  };

  const kayitlar = sorgu.data?.kayitlar ?? [];

  return (
    <Sayfa>
      <SayfaBasligi
        baslik="Denetim kaydı"
        aciklama="Kim, ne zaman, hangi kaydı nasıl değiştirdi. Kayıtlar değiştirilemez ve silinemez; yalnızca eklenir."
      />

      <Uyari tur="bilgi" baslik="Salt okunur">
        Bu ekranda düzenleme veya silme yoktur. Denetim kaydı yalnızca eklenir;
        veritabanı seviyesinde de güncelleme/silme yetkisi tanımlı değildir.
        {sorgu.data && (
          <>
            {" "}
            Tarih süzgeci okul saat diliminde çözülür:{" "}
            <span className="font-mono">{sorgu.data.saat_dilimi}</span>.
          </>
        )}
      </Uyari>

      <FiltreSeridi>
        <MetinSuzgeci
          id="audit-eylem"
          etiket="Eylem"
          deger={filtre.eylem ?? ""}
          degistir={(v) => guncelle({ eylem: v })}
          ipucu="Tam eşleşme, örn. mutabakat.cozum"
        />
        <SecimSuzgeci
          id="audit-aktor"
          etiket="Aktör tipi"
          deger={filtre.aktor_tipi ?? ""}
          degistir={(v) => guncelle({ aktor_tipi: v })}
          secenekler={AKTOR_SECENEKLERI}
        />
        <MetinSuzgeci
          id="audit-varlik"
          etiket="Varlık"
          deger={filtre.varlik ?? ""}
          degistir={(v) => guncelle({ varlik: v })}
          ipucu="örn. terminal, nokta, kisi"
        />
        <TarihAlani
          id="audit-bas"
          etiket="Başlangıç"
          deger={filtre.bas ?? ""}
          degistir={(v) => guncelle({ bas: v })}
        />
        <TarihAlani
          id="audit-bit"
          etiket="Bitiş"
          deger={filtre.bit ?? ""}
          degistir={(v) => guncelle({ bit: v })}
        />
      </FiltreSeridi>

      <SorguDurumu
        yukleniyor={sorgu.isLoading}
        hata={sorgu.error}
        bos={!sorgu.isLoading && kayitlar.length === 0}
        bosBaslik="Denetim kaydı yok"
        bosAciklama="Bu süzgeçte kayıt bulunamadı. Tarih aralığını genişletmeyi deneyin."
        iskeletSatir={8}
        yenile={() => void sorgu.refetch()}
      >
        <Tablo>
          <TabloBaslik>
            <th scope="col">Zaman</th>
            <th scope="col">Aktör</th>
            <th scope="col">Eylem</th>
            <th scope="col">Varlık</th>
            <th scope="col">Sonuç</th>
            <th scope="col">IP</th>
            <th scope="col">Ayrıntı</th>
          </TabloBaslik>
          <TabloGovde>
            {kayitlar.map((k) => (
              <tr key={k.id}>
                <td className="rakam whitespace-nowrap font-mono text-xs">
                  {tarihSaat(k.olusma_zamani)}
                </td>
                <td>
                  <span className="block text-murekkep">
                    {k.aktor_ad ?? "—"}
                  </span>
                  <span className="text-xs text-murekkep-3">
                    {AKTOR_TIPI_ADI[k.aktor_tipi] ?? k.aktor_tipi}
                    {k.aktor_id && (
                      <span className="rakam ml-1 font-mono">
                        #{k.aktor_id}
                      </span>
                    )}
                  </span>
                </td>
                <td className="font-mono text-xs">{k.eylem}</td>
                <td className="text-xs">
                  {k.varlik ?? "—"}
                  {k.varlik_id && (
                    <span className="rakam ml-1 font-mono text-murekkep-3">
                      #{k.varlik_id}
                    </span>
                  )}
                </td>
                <td>
                  {k.basarili ? (
                    <Rozet tur="onay">başarılı</Rozet>
                  ) : (
                    <Rozet tur="hata">başarısız</Rozet>
                  )}
                </td>
                <td className="rakam font-mono text-xs">{k.ip ?? "—"}</td>
                <td>
                  {k.aciklama && (
                    <p className="m-0 max-w-prose text-xs text-murekkep-2">
                      {k.aciklama}
                    </p>
                  )}
                  {(k.onceki !== null || k.sonraki !== null) && (
                    <details className="mt-1">
                      <summary className="cursor-pointer text-xs text-murekkep-3">
                        Önceki / sonraki
                      </summary>
                      <div className="mt-1 grid gap-2 md:grid-cols-2">
                        <div>
                          <p className="m-0 text-2xs text-murekkep-3">Önceki</p>
                          <pre className="m-0 max-h-48 overflow-auto rounded-sm bg-kagit-2 p-2 text-2xs text-murekkep-2">
                            {jsonGoster(k.onceki)}
                          </pre>
                        </div>
                        <div>
                          <p className="m-0 text-2xs text-murekkep-3">
                            Sonraki
                          </p>
                          <pre className="m-0 max-h-48 overflow-auto rounded-sm bg-kagit-2 p-2 text-2xs text-murekkep-2">
                            {jsonGoster(k.sonraki)}
                          </pre>
                        </div>
                      </div>
                    </details>
                  )}
                </td>
              </tr>
            ))}
          </TabloGovde>
        </Tablo>

        <Sayfalayici
          sayfa={sorgu.data?.sayfa ?? 1}
          boyut={sorgu.data?.sayfa_boyu ?? 50}
          toplam={sorgu.data?.toplam ?? 0}
          degistir={setSayfa}
        />
      </SorguDurumu>
    </Sayfa>
  );
}
